<script>
import WebLayout from '@/layouts/Web/Layout.vue'

export default {
  layout: WebLayout,
}
</script>

<script setup>
import { computed, onMounted, nextTick } from 'vue'
import SEOHead from '@/components/SEOHead.vue'
import { useWebTranslations } from '@/composables/useWebTranslations.js'
import { useScrollReveal } from '@/composables/useScrollReveal.js'

defineProps({
  cards: { type: Array, default: () => [] },
})

const { get, locale, webRoute, alternateLocales } = useWebTranslations()
const { refresh } = useScrollReveal()

const seoTitle = computed(() => get('static.links.seo_title'))
const seoDescription = computed(() => get('static.links.seo_description'))
const canonicalPath = computed(() => webRoute('web.links'))
const pageAlternateLocales = computed(() => alternateLocales('web.links'))

onMounted(async () => {
  await nextTick()
  refresh()
})
</script>

<template>
  <SEOHead
    :title="seoTitle"
    :description="seoDescription"
    :canonical="canonicalPath"
    :locale="locale"
    :alternate-locales="pageAlternateLocales"
  />

  <section class="gw-paper border-b gw-rule">
    <div class="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <p
        class="gw-mono text-[10.5px] font-medium uppercase tracking-[0.22em] text-[rgba(51,51,51,0.5)] mb-3"
        data-reveal
      >
        {{ get('static.links.eyebrow') }}
      </p>
      <h1 class="gw-title max-w-3xl" data-reveal>
        {{ get('static.links.heading') }}
      </h1>
      <p class="mt-5 text-[1.05rem] leading-relaxed text-[color:var(--gw-ink-soft)] max-w-2xl" data-reveal>
        {{ get('static.links.lead') }}
      </p>
      <p class="mt-3 gw-mono text-[10.5px] uppercase tracking-[0.18em] text-[rgba(51,51,51,0.45)]" data-reveal>
        {{ cards.length }} {{ get('static.links.count_label') }}
      </p>
    </div>
  </section>

  <section class="gw-paper-soft">
    <div class="max-w-6xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
      <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
        <a
          v-for="card in cards"
          :key="card.topic"
          :href="card.path"
          class="group block rounded-2xl border gw-rule-strong bg-white p-6 transition-all duration-300 hover:shadow-[0_18px_40px_rgba(51,51,51,0.08)] hover:border-[rgba(255,67,160,0.35)]"
          data-reveal
        >
          <div class="flex items-center justify-between gap-3">
            <span class="gw-mono text-[11px] font-semibold tabular-nums tracking-[0.08em] text-[color:var(--gw-ink)]">
              {{ String(card.number).padStart(2, '0') }}
            </span>
            <span class="gw-mono text-[10px] font-medium uppercase tracking-[0.18em] text-[rgba(51,51,51,0.45)]">
              {{ card.locale }}
            </span>
          </div>
          <h2 class="mt-3 text-[1.05rem] font-semibold tracking-[-0.02em] text-[color:var(--gw-ink)] leading-snug group-hover:text-[color:var(--gw-pink)] transition-colors">
            {{ card.title }}
          </h2>
          <p class="mt-2 text-sm leading-relaxed text-[color:var(--gw-ink-soft)] line-clamp-3">
            {{ card.description }}
          </p>
          <p class="mt-4 gw-mono text-[10.5px] text-[rgba(51,51,51,0.45)] tracking-[0.04em]">
            {{ card.path }}
          </p>
        </a>
      </div>
    </div>
  </section>
</template>
